export const SCRIPT_CONTENT_BEAT_TYPES = [
  "order_show",
  "effect_need",
  "value_price",
  "question",
  "life_scene",
  "praise",
  "general",
] as const;

export type ScriptContentBeatType = (typeof SCRIPT_CONTENT_BEAT_TYPES)[number];

export const SCRIPT_CONTENT_BEAT_LABELS: Record<ScriptContentBeatType, string> = {
  order_show: "晒单",
  effect_need: "效果诉求",
  value_price: "性价比",
  question: "提问",
  life_scene: "生活场景",
  praise: "夸赞背书",
  general: "综合",
};

/** 晒单类：近 N 个已分配晒单角色内禁止重复同一 wxid */
export const ORDER_SHOW_LOOKBACK_ROLES = 3;

/** 全场单账号出场次数上限 */
export const MAX_MEMBER_APPEARANCES_CAP = 5;

const BEAT_CLASSIFICATION_RULES: Array<{
  beat: ScriptContentBeatType;
  patterns: RegExp[];
}> = [
  {
    beat: "order_show",
    // 「订单图」仅指 C 列纯文字；图片理解行已在分类前剔除
    patterns: [/订单图/, /下单\s*\d+/, /拍了\s*\d+/, /拍\s*\d+/, /晒单/],
  },
  {
    beat: "question",
    patterns: [/还能拍吗/, /有用吗/, /怎么样/, /能不能/, /可以吗/, /吗[？?]/],
  },
  {
    beat: "value_price",
    patterns: [/划算/, /性价比/, /便宜/, /才\d+多/, /福利价/, /特价/, /超级值/],
  },
  {
    beat: "effect_need",
    patterns: [/改善/, /黑眼圈/, /干纹/, /眼袋/, /松弛/, /浮肿/, /预防/, /细纹/],
  },
  {
    beat: "life_scene",
    patterns: [/带娃/, /上了年纪/, /睡前/, /天天/, /岁数/],
  },
  {
    beat: "praise",
    patterns: [/大牌/, /成分/, /鼓掌/, /\[强\]/, /兜底/],
  },
];

export interface PriorRoleAssignment {
  tempRoleId: string;
  wxid: string;
  contentBeat: ScriptContentBeatType;
  firstRowIndex: number;
  lastRowIndex?: number;
  fullText?: string;
}

export function contentHasReorderIntent(text: string): boolean {
  return /再加单|再买点/.test(text);
}

export function getScriptContentBeatLabel(beat: ScriptContentBeatType): string {
  return SCRIPT_CONTENT_BEAT_LABELS[beat];
}

/** 分类时忽略图片理解行（`[图片] …`），仅按 C 列纯文字判断话术类型 */
export function stripScriptImageDescriptionsForClassification(text: string): string {
  return text
    .split("\n")
    .filter((line) => !/^\[图片\]/.test(line.trim()))
    .join("\n")
    .trim();
}

export function classifyScriptContentBeat(fullText: string): ScriptContentBeatType {
  const text = stripScriptImageDescriptionsForClassification(fullText);
  if (!text) return "general";

  for (const rule of BEAT_CLASSIFICATION_RULES) {
    if (rule.patterns.some((pattern) => pattern.test(text))) {
      return rule.beat;
    }
  }

  return "general";
}

/** 按当前台词重算 contentBeat；无台词时保留 fallback */
export function resolveScriptRoleContentBeat(
  fullText: string | undefined,
  fallback: ScriptContentBeatType = "general"
): ScriptContentBeatType {
  const text = fullText?.trim() ?? "";
  if (!text) return fallback;
  return classifyScriptContentBeat(text);
}

export function reclassifyPriorAssignmentsContentBeat(
  priorAssignments: PriorRoleAssignment[]
): PriorRoleAssignment[] {
  return priorAssignments.map((item) => ({
    ...item,
    contentBeat: resolveScriptRoleContentBeat(item.fullText, item.contentBeat),
  }));
}

/** 晒单 lookback：取当前角色之前、最近 N 个已分配晒单角色 */
export function getOrderShowLookbackPriorAssignments(
  priorBeforeCurrent: PriorRoleAssignment[]
): PriorRoleAssignment[] {
  return priorBeforeCurrent
    .filter((prior) => prior.contentBeat === "order_show")
    .slice(-ORDER_SHOW_LOOKBACK_ROLES);
}

export function areScriptBeatsSimilar(
  left: ScriptContentBeatType,
  right: ScriptContentBeatType
): boolean {
  if (left === "general" || right === "general") return false;
  if (left === right) return true;
  if (
    (left === "effect_need" && right === "life_scene") ||
    (left === "life_scene" && right === "effect_need")
  ) {
    return true;
  }
  return false;
}

export function computeMaxMemberAppearances(
  totalRoleCount: number,
  memberCount: number
): number {
  if (memberCount <= 0 || totalRoleCount <= 0) return 0;
  return MAX_MEMBER_APPEARANCES_CAP;
}

function getPriorBeforeCurrent(priorAssignments: PriorRoleAssignment[], firstRowIndex: number) {
  const sortedPrior = [...priorAssignments].sort(
    (left, right) => left.firstRowIndex - right.firstRowIndex
  );
  return sortedPrior.filter((item) => item.firstRowIndex < firstRowIndex);
}

export function resolvePriorLastRowIndex(
  assignment: PriorRoleAssignment,
  bundleLastRowIndex?: number
): number {
  return assignment.lastRowIndex ?? bundleLastRowIndex ?? assignment.firstRowIndex;
}

/** 上一段剧本与当前段在表格行上接壤（中间无空行间隔） */
export function areAdjacentScriptRoleBlocks(
  previous: PriorRoleAssignment,
  currentFirstRowIndex: number
): boolean {
  return resolvePriorLastRowIndex(previous) + 1 === currentFirstRowIndex;
}

/**
 * 晒单类：不得复用此前已分配晒单角色的 wxid（除非当前内容提到再加单/再买点）。
 * 仅作用于晒单 → 晒单；已晒单账号仍可匹配提问、生活场景等其他话术。
 */
export function getOrderShowHistoryExcludedWxids(input: {
  contentBeat: ScriptContentBeatType;
  currentFullText?: string;
  firstRowIndex: number;
  priorAssignments: PriorRoleAssignment[];
}): Set<string> {
  const excluded = new Set<string>();
  if (input.contentBeat !== "order_show") {
    return excluded;
  }
  if (contentHasReorderIntent(input.currentFullText ?? "")) {
    return excluded;
  }

  for (const item of getPriorBeforeCurrent(input.priorAssignments, input.firstRowIndex)) {
    if (item.contentBeat === "order_show") {
      excluded.add(item.wxid);
    }
  }

  return excluded;
}

export function getDiversityExcludedWxids(input: {
  contentBeat: ScriptContentBeatType;
  firstRowIndex: number;
  priorAssignments: PriorRoleAssignment[];
  totalRoleCount: number;
  memberCount: number;
  currentFullText?: string;
}): Set<string> {
  const excluded = new Set<string>();
  const sortedPrior = [...input.priorAssignments].sort(
    (left, right) => left.firstRowIndex - right.firstRowIndex
  );
  const priorBeforeCurrent = getPriorBeforeCurrent(
    input.priorAssignments,
    input.firstRowIndex
  );

  for (const wxid of getOrderShowHistoryExcludedWxids({
    contentBeat: input.contentBeat,
    currentFullText: input.currentFullText,
    firstRowIndex: input.firstRowIndex,
    priorAssignments: input.priorAssignments,
  })) {
    excluded.add(wxid);
  }

  const previous = priorBeforeCurrent.at(-1);
  if (previous) {
    if (areAdjacentScriptRoleBlocks(previous, input.firstRowIndex)) {
      excluded.add(previous.wxid);
    } else if (areScriptBeatsSimilar(input.contentBeat, previous.contentBeat)) {
      excluded.add(previous.wxid);
    }
  }

  if (
    input.contentBeat === "order_show" &&
    !contentHasReorderIntent(input.currentFullText ?? "")
  ) {
    for (const item of getOrderShowLookbackPriorAssignments(priorBeforeCurrent)) {
      excluded.add(item.wxid);
    }
  }

  const maxAppearances = computeMaxMemberAppearances(
    input.totalRoleCount,
    input.memberCount
  );
  const usageCount = new Map<string, number>();
  for (const item of sortedPrior) {
    usageCount.set(item.wxid, (usageCount.get(item.wxid) ?? 0) + 1);
  }
  for (const [wxid, count] of usageCount) {
    if (count >= maxAppearances) {
      excluded.add(wxid);
    }
  }

  return excluded;
}

export function validateAssignmentAgainstDiversity(input: {
  wxid: string;
  contentBeat: ScriptContentBeatType;
  firstRowIndex: number;
  priorAssignments: PriorRoleAssignment[];
  totalRoleCount: number;
  memberCount: number;
  currentFullText?: string;
}):
  | { ok: true }
  | { ok: false; reason: string } {
  const orderShowExcluded = getOrderShowHistoryExcludedWxids(input);
  if (orderShowExcluded.has(input.wxid)) {
    return {
      ok: false,
      reason: `账号 ${input.wxid} 违反晒单互斥（该账号此前已分配其他晒单角色；晒单账号仍可匹配非晒单话术）`,
    };
  }

  const excluded = getDiversityExcludedWxids(input);
  if (excluded.has(input.wxid)) {
    return {
      ok: false,
      reason: `账号 ${input.wxid} 违反多样性约束（${getScriptContentBeatLabel(input.contentBeat)}）`,
    };
  }
  return { ok: true };
}

export function formatDiversityContextForMatching(input: {
  contentBeat: ScriptContentBeatType;
  priorAssignments: PriorRoleAssignment[];
  totalRoleCount: number;
  memberCount: number;
  memberNumberByWxid: Map<string, string>;
  eligibleCandidateCount: number;
}): string {
  const resolveNumber = (wxid: string) => input.memberNumberByWxid.get(wxid)?.trim() || "";

  const lines = [
    `contentBeat: ${input.contentBeat}（${getScriptContentBeatLabel(input.contentBeat)}）`,
    `totalTempRoles: ${input.totalRoleCount}`,
    `maxAppearancesPerMember: ${computeMaxMemberAppearances(input.totalRoleCount, input.memberCount)}`,
    `eligibleCandidates: ${input.eligibleCandidateCount}（已达上限或违反多样性者已从候选列表剔除，不在上下文中出现）`,
  ];

  const priorBeforeCurrent = input.priorAssignments
    .slice()
    .sort((left, right) => left.firstRowIndex - right.firstRowIndex);

  if (priorBeforeCurrent.length > 0) {
    lines.push(
      "priorAssignments:",
      ...priorBeforeCurrent.map((item) => {
        const number = resolveNumber(item.wxid);
        const assignee = number ? `编号${number}` : "（编号未知）";
        return `- 气氛员${item.tempRoleId} → ${assignee}（${getScriptContentBeatLabel(item.contentBeat)}）`;
      })
    );
  }

  return lines.join("\n");
}
